import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { AvatarEmotion, detectEmotionFromText } from '@/hooks/useAvatarPreferences';

interface AvatarEmotionContextType {
  currentEmotion: AvatarEmotion;
  setEmotion: (emotion: AvatarEmotion) => void;
  detectAndSetEmotion: (text: string) => AvatarEmotion;
  isSpeaking: boolean;
  setIsSpeaking: (speaking: boolean) => void;
  audioLevel: number;
  setAudioLevel: (level: number) => void;
}

const AvatarEmotionContext = createContext<AvatarEmotionContextType | undefined>(undefined);

// Auto-reset to neutral after inactivity
const EMOTION_RESET_DELAY = 8000; // 8 seconds

export const AvatarEmotionProvider = ({ children }: { children: ReactNode }) => {
  const [currentEmotion, setCurrentEmotion] = useState<AvatarEmotion>('neutral');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const emotionTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Clear any existing timeout and schedule reset to neutral
  const scheduleReset = useCallback(() => {
    if (emotionTimeoutRef.current) {
      clearTimeout(emotionTimeoutRef.current);
    }
    emotionTimeoutRef.current = setTimeout(() => {
      setCurrentEmotion('neutral');
    }, EMOTION_RESET_DELAY);
  }, []);

  const setEmotion = useCallback((emotion: AvatarEmotion) => {
    setCurrentEmotion(emotion);
    if (emotion !== 'neutral') {
      scheduleReset();
    }
  }, [scheduleReset]);

  const detectAndSetEmotion = useCallback((text: string): AvatarEmotion => {
    const detected = detectEmotionFromText(text);
    setEmotion(detected);
    return detected;
  }, [setEmotion]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (emotionTimeoutRef.current) {
        clearTimeout(emotionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <AvatarEmotionContext.Provider
      value={{
        currentEmotion,
        setEmotion,
        detectAndSetEmotion,
        isSpeaking,
        setIsSpeaking,
        audioLevel,
        setAudioLevel,
      }}
    >
      {children}
    </AvatarEmotionContext.Provider>
  );
};

export const useAvatarEmotion = (): AvatarEmotionContextType => {
  const context = useContext(AvatarEmotionContext);
  if (!context) {
    throw new Error('useAvatarEmotion must be used within an AvatarEmotionProvider');
  }
  return context;
};

// Hook that safely returns defaults when used outside provider (for optional usage)
export const useAvatarEmotionSafe = (): AvatarEmotionContextType => {
  const context = useContext(AvatarEmotionContext);
  if (!context) {
    // Return safe defaults when outside provider
    return {
      currentEmotion: 'neutral' as AvatarEmotion,
      setEmotion: () => {},
      detectAndSetEmotion: () => 'neutral' as AvatarEmotion,
      isSpeaking: false,
      setIsSpeaking: () => {},
      audioLevel: 0,
      setAudioLevel: () => {},
    };
  }
  return context;
};
